import Phaser from "phaser";
import { BattleEngine, unitKey, type BattleEvent, type Side, type UnitId } from "../model/battle";
import { damageForTier, elementMultiplier, hpForTier } from "../model/balance";
import { finishBattle, type Run } from "../model/run";
import { saveRun } from "../persistence";
import { setGameStatus } from "../status";
import { drawBattlefield, layoutFor, makeTeam } from "../view/BattlefieldView";
import { icon, PictureButton } from "../view/Controls";
import { Effects } from "../view/Effects";
import { TeamStatusView } from "../view/TeamStatusView";
import { PieceView } from "../view/PieceView";
import { motionEnabled } from "../view/preferences";
import { gameAudio } from "../view/Audio";
import { project } from "../view/iso";
import { stageX, stageY } from "../view/stage";
import { openOnlineChat, requestLeaveMatch } from "../view/OnlineOverlay";
import { multiplayer } from "../multiplayer";

export class BattleScene extends Phaser.Scene {
  private run!: Run;
  private battle!: BattleEngine;
  private views = new Map<string, PieceView>();
  private effects!: Effects;
  private elapsed = 0;
  private savedAt = 0;
  private shimmerAt = 0;
  private shimmerIndex = 0;
  private speed = 1;
  private ended = false;
  private motion = true;
  private topRightButton!: PictureButton;
  private multiplayerMode = false;
  private defeated = new Set<string>();
  private ownSide: Side = "player";
  private visualHp = new Map<string, number>();
  private pendingHits = new Map<string, number>();
  private projectiles = 0;
  private countdown?: Phaser.GameObjects.Text;
  private countdownValue = -1;
  private teams?: TeamStatusView;
  private pendingEnd: Extract<BattleEvent, { type: "ended" }> | null = null;
  constructor() { super("BattleScene"); }
  init(data: { run: Run; online?: boolean }): void {
    this.countdown = undefined; this.countdownValue = -1; this.teams = undefined;
    this.run = data.run; this.elapsed = this.savedAt = this.shimmerAt = this.shimmerIndex = 0;
    this.speed = 1; this.ended = false; this.views = new Map(); this.motion = motionEnabled();
    this.multiplayerMode = Boolean(data.online); this.defeated = new Set();
    this.ownSide = this.multiplayerMode ? multiplayer.match!.ownSide : "player";
    this.visualHp = new Map(); this.pendingHits = new Map(); this.projectiles = 0; this.pendingEnd = null;
  }
  create(): void {
    this.tweens.timeScale = 1;
    drawBattlefield(this, this.run.level);
    this.effects = new Effects(this, this.motion);
    if (!this.multiplayerMode) {
      this.battle = new BattleEngine(this.run.playerBoard, this.run.opponentBoard, this.run.rngState);
      if (this.run.battle) this.battle.restore(this.run.battle);
      this.elapsed = this.savedAt = this.battle.time;
    }
    for (const side of ["player", "opponent"] as const) {
      const board = side === this.ownSide ? this.run.playerBoard : this.run.opponentBoard;
      makeTeam(this, board, side, true, this.motion).forEach((view, pieceId) => this.views.set(unitKey({ side, pieceId }), view));
    }
    for (const unit of this.multiplayerMode ? multiplayer.latestFrame?.units ?? [] : this.battle.units) {
      const view = this.views.get(unitKey(unit))!;
      view.setHealth(unit.hp, true); this.visualHp.set(unitKey(unit), unit.hp);
      view.setCharges(unit.charges, false);
      if (!unit.hp) { view.defeat(); this.defeated.add(unitKey(unit)); }
      else if (unit.side !== this.ownSide && this.elapsed < 100) view.appear();
    }
    const sound = new PictureButton(this, 1380, 66, gameAudio.enabled ? "speaker" : "muted", "Toggle sound", () => {
      gameAudio.toggle(); sound.setIcon(gameAudio.enabled ? "speaker" : "muted");
    });
    if (this.multiplayerMode) new PictureButton(this, 1290, 66, "chat", "Open chat", openOnlineChat, 60);
    this.topRightButton = new PictureButton(this, 1470, 66, this.multiplayerMode ? "exit" : "speed",
      this.multiplayerMode ? "Leave multiplayer match" : "Toggle faster battle", () => {
      if (this.multiplayerMode) { requestLeaveMatch(); return; }
      if (this.ended) return;
      this.speed = this.speed === 1 ? 2 : 1;
      this.tweens.timeScale = this.speed; this.effects.setSpeed(this.speed);
      this.topRightButton.setActive(this.speed === 2);
    });
    if (this.multiplayerMode) {
      this.teams = new TeamStatusView(this, this.effects, this.motion, multiplayer.match!);
      this.countdown = this.add.text(stageX(768), stageY(430), "", {
        fontFamily: "Georgia, serif", fontSize: "100px", color: "#ffedac", stroke: "#213b34", strokeThickness: 10,
      }).setOrigin(0.5).setDepth(700);
      this.updateCountdown();
    }
    const pointerAttack = (_pointer: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]) => {
      // Buttons handle their own clicks; every other part of the canvas attacks.
      if (!over.length) this.fire();
    };
    const keyAttack = (event: KeyboardEvent) => {
      if (event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && (focused.isContentEditable || focused.matches("input, textarea, select"))) return;
      if (this.multiplayerMode && event.key === "Enter") {
        return; // The shared chat overlay handles Enter.
      }
      const activatesButton = event.key === " " || event.key === "Enter";
      if (activatesButton && focused instanceof HTMLButtonElement) return;
      if (event.key.length === 1 || event.key === "Enter") { event.preventDefault(); this.fire(); }
    };
    const checkpoint = () => this.persist();
    this.input.on("pointerdown", pointerAttack);
    this.input.keyboard?.on("keydown", keyAttack);
    window.addEventListener("pagehide", checkpoint);
    document.addEventListener("visibilitychange", checkpoint);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.off("pointerdown", pointerAttack); this.input.keyboard?.off("keydown", keyAttack);
      window.removeEventListener("pagehide", checkpoint); document.removeEventListener("visibilitychange", checkpoint);
      this.views.forEach(view => view.destroy());
    });
    if (!this.multiplayerMode && this.battle.winner !== null) this.present({ type: "ended", at: this.battle.time, winner: this.battle.winner });
    else if (!this.multiplayerMode || multiplayer.battleCountdownRemaining === 0) setGameStatus("ready", `Battle underway. Level ${this.run.level}. Tap to attack.`);
  }
  update(time: number, delta: number): void {
    this.views.forEach(view => view.update(time));
    if (this.ended || document.hidden) return;
    if (this.multiplayerMode) {
      this.updateCountdown();
      for (const frame of multiplayer.takeFrames()) {
        for (const event of frame.events) this.present(event);
      }
      const frame = multiplayer.latestFrame;
      if (frame) {
        for (const unit of frame.units) {
          const view = this.views.get(unitKey(unit));
          if (!view) throw new Error(`Battle frame contains an unknown unit: ${unitKey(unit)}`);
          // A server snapshot cannot move a bar before its visible projectile lands.
          if (!this.pendingHits.get(unitKey(unit))) this.setOnlineHealth(unit, unit.hp);
          view.setCharges(unit.charges, !this.ended);
        }
        if (frame.winner !== null && !this.ended) this.present({ type: "ended", at: frame.elapsed, winner: frame.winner });
      }
    } else {
      this.elapsed += delta * this.speed;
      this.battle.advance(this.elapsed).forEach(event => this.present(event));
      this.syncCharges();
      if (this.battle.time - this.savedAt >= 1000) { this.persist(); this.savedAt = this.battle.time; }
    }
    if (this.motion && time - this.shimmerAt > 220) {
      this.shimmerAt = time;
      const alive = (this.multiplayerMode ? multiplayer.latestFrame?.units ?? [] : this.battle.units).filter(unit => unit.hp > 0);
      const unit = alive[this.shimmerIndex++ % alive.length];
      if (unit) this.effects.idle(unit.piece.element, this.views.get(unitKey(unit))!.focusPoint());
    }
  }
  private updateCountdown(): void {
    const remaining = multiplayer.battleCountdownRemaining;
    const value = Math.ceil(remaining / 1000);
    if (value === this.countdownValue) return;
    this.countdownValue = value;
    this.views.forEach(view => view.setReady(value > 0));
    if (!this.countdown) return;
    this.tweens.killTweensOf(this.countdown);
    this.countdown.setScale(1).setAlpha(1).setText(value > 0 ? String(value) : "");
    if (value > 0) {
      setGameStatus("ready", `Both teams ready. Battle starts in ${value}.`);
      if (this.motion) {
        this.countdown.setScale(1.25);
        this.tweens.add({ targets: this.countdown, scale: 1, duration: 350, ease: "Back.Out" });
        this.effects.burst("lightning", this.countdown.x, this.countdown.y + 60, 1.2);
      }
    } else {
      this.teams?.battleStarted();
      setGameStatus("ready", "Battle underway. Tap to attack.");
      this.tweens.add({ targets: this.countdown, alpha: 0, duration: 600 });
    }
  }
  private fire(): void {
    if (this.ended || (this.multiplayerMode && multiplayer.battleCountdownRemaining > 0)) return;
    gameAudio.unlock();
    if (this.multiplayerMode) {
      multiplayer.attack();
      return;
    }
    if (!this.battle.requestPlayerAttack()) return;
    this.syncCharges();
    this.persist();
  }

  private persist(): void {
    if (this.multiplayerMode) return;
    this.run = { ...this.run, battle: this.battle.progress }; saveRun(this.run);
  }
  private syncCharges(): void {
    this.battle.units.forEach(unit => this.views.get(unitKey(unit))!.setCharges(this.ended ? 0 : unit.charges, !this.ended));
  }
  private setOnlineHealth(unit: UnitId, hp: number, hit = false): void {
    const key = unitKey(unit), view = this.views.get(key)!;
    const before = this.visualHp.get(key) ?? hpForTier(view.piece.tier);
    // Health only decreases during a battle; an older in-flight snapshot cannot undo a local impact.
    const next = Math.max(0, Math.min(before, hp));
    this.visualHp.set(key, next); view.setHealth(next, true);
    if (hit && next < before) view.hit(unit.side === "player" ? 1 : -1);
    if (!next && !this.defeated.has(key)) { view.defeat(); this.defeated.add(key); }
  }
  private present(event: BattleEvent): void {
    if (this.ended) return;
    if (event.type === "attack") {
      const actor = this.views.get(unitKey(event.actor))!, target = this.views.get(unitKey(event.target))!;
      const to = target.focusPoint(), from = actor.attackPoint(to);
      if (this.multiplayerMode) {
        this.projectiles++;
        for (const victim of event.affected) { const key = unitKey(victim); this.pendingHits.set(key, (this.pendingHits.get(key) ?? 0) + 1); }
      }
      actor.attack(to); gameAudio.attack(event.element, event.tier);
      this.effects.projectile(event.element, from, to, event.tier, event.impactAt - event.at, () => {
        if (event.area.length > 1) this.effects.area(event.element, event.area.map(pos => project(pos, layoutFor(this, event.target.side))), event.tier);
        if (this.multiplayerMode) {
          // Target and damage are already deterministic at launch. Apply them in the
          // exact callback that draws the impact; the worker still decides the result.
          for (const victim of event.affected) {
            const key = unitKey(victim), view = this.views.get(key)!;
            const damage = Math.round(damageForTier(event.tier) * elementMultiplier(event.element, view.piece.element));
            this.pendingHits.set(key, Math.max(0, (this.pendingHits.get(key) ?? 1) - 1));
            this.setOnlineHealth(victim, (this.visualHp.get(key) ?? 0) - damage, true);
            const frame = multiplayer.latestFrame;
            if (!this.pendingHits.get(key) && frame && frame.elapsed >= event.impactAt) {
              const authoritative = frame.units.find(unit => unitKey(unit) === key);
              if (authoritative) this.setOnlineHealth(victim, authoritative.hp);
            }
          }
          this.projectiles--;
          if (!this.projectiles && this.pendingEnd) { const end = this.pendingEnd; this.pendingEnd = null; this.present(end); }
        }
      });
    } else if (event.type === "charged") {
      // Persistent sparks are synchronized from the actual bank, including reserved shots.
      return;
    } else if (event.type === "damage") {
      const target = this.views.get(unitKey(event.target))!;
      if (this.multiplayerMode) {
        if (!this.pendingHits.get(unitKey(event.target))) this.setOnlineHealth(event.target, event.hp, true);
      } else { target.setHealth(event.hp, true); target.hit(event.target.side === "player" ? 1 : -1); }
    } else if (event.type === "defeated") {
      if (this.multiplayerMode && this.pendingHits.get(unitKey(event.target))) return;
      if (this.defeated.has(unitKey(event.target))) return;
      this.views.get(unitKey(event.target))!.defeat(); this.defeated.add(unitKey(event.target));
    } else {
      if (this.multiplayerMode && this.projectiles) { this.pendingEnd = event; return; }
      this.ended = true;
      if (!this.multiplayerMode) this.topRightButton.setEnabled(false);
      this.tweens.timeScale = 1; this.effects.setSpeed(1);
      const won = event.winner === this.ownSide, breakthrough = won && this.run.attempts > 5;
      const winningLayout = event.winner === "draw" ? null : layoutFor(this, event.winner);
      const x = winningLayout?.originX ?? stageX(768);
      const y = winningLayout ? winningLayout.originY - stageY(event.winner === "player" ? 92 : 122) : stageY(184);
      const radius = breakthrough ? 115 : won ? 93 : 58;
      const medal = this.add.container(x, y).setDepth(650);
      const rim = this.add.graphics().fillStyle(0x263f36, 0.97).fillCircle(0, 0, radius);
      rim.lineStyle(won ? 5 : 3, 0xc7ac73).strokeCircle(0, 0, radius);
      rim.lineStyle(2, 0xffe4aa, 0.6).strokeCircle(0, 0, radius - 7); medal.add(rim);
      if (won) {
        const laurel = this.add.graphics().lineStyle(4, 0xe4c781);
        for (const sign of [-1, 1]) {
          laurel.beginPath().moveTo(sign * 8, 68);
          for (let i = 0; i <= 12; i++) {
            const angle = Math.PI * (0.04 + i * 0.06);
            laurel.lineTo(sign * Math.sin(angle) * 72, Math.cos(angle) * 66);
          }
          laurel.strokePath();
          for (let i = 0; i < 6; i++) {
            const angle = Math.PI * (0.14 + i * 0.12), lx = sign * Math.sin(angle) * 72, ly = Math.cos(angle) * 66;
            laurel.fillStyle(i % 2 ? 0xffdda0 : 0xcba86c).fillEllipse(lx, ly, 16, 9);
          }
        }
        medal.add(laurel);
      }
      medal.add(icon(this, event.winner === "draw" ? "gift" : "trophy", won ? 100 : 76));
      if (this.motion) {
        medal.setScale(0.3).setAngle(-8);
        this.tweens.add({ targets: medal, scale: 1, angle: 0, duration: 520, ease: "Back.Out" });
      }
      if (won) this.effects.victory(x, y, breakthrough);
      gameAudio.result(won, breakthrough);
      let delay = 0;
      this.views.forEach((view, key) => {
        if (!key.startsWith(`${event.winner}:`)) return;
        view.celebrate(delay); delay += 45;
        this.effects.burst(view.piece.element, view.root.x, view.root.y - 40, 1.1);
      });
      this.persist();
      setGameStatus("ready", `Battle ended. ${event.winner === "draw" ? "Draw" : won ? "Victory" : "Retry"}.`);
      this.time.delayedCall(breakthrough ? 4900 : won ? 2300 : 1500, () => {
        if (this.multiplayerMode) {
          const run = multiplayer.match?.own.run;
          if (!run || run.phase === "battle") { multiplayer.fail("The server did not deliver the battle result. Reconnecting…"); return; }
          this.scene.start("BoardScene", { run, online: true });
        } else {
          this.run = finishBattle(this.run, event.winner, this.battle.rngState);
          saveRun(this.run); this.scene.start("BoardScene", { run: this.run });
        }
      });
    }
  }
}
